import { Fragment } from "react";
import { motion } from "framer-motion";
import { ChevronRight, Home, Droplets, Gauge, Waves, Filter, Sun, Building2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const flow = [
  { label: "Roof Catchment", icon: Building2 },
  { label: "Tank", icon: Droplets },
  { label: "Pump", icon: Gauge },
  { label: "Pipework", icon: Waves },
  { label: "Filtration", icon: Filter },
  { label: "UV", icon: Sun },
  { label: "Household", icon: Home },
];

export default function CompleteCare() {
  return (
    <section className="py-14 lg:py-20 bg-white overflow-hidden" data-testid="complete-care-section">
      <div className="mx-auto max-w-7xl px-5 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-pristine-aquadeep mb-4">The System Approach</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-pristine-navy tracking-tight">
              More Than Tank Cleaning
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base sm:text-lg text-pristine-navy/70 leading-relaxed">
              Your tank, roof catchment, gutters, pumps, pipework, filters and UV equipment all work
              together. Treating only one part of the system may not address the underlying issue. Our
              team assesses the complete water pathway and recommends practical improvements based on
              your property.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-wrap items-center gap-y-3">
            {flow.map((f, i) => {
              const Icon = f.icon;
              return (
                <Fragment key={f.label}>
                  <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08, duration: 0.5 }}
                    className="inline-flex items-center gap-2 rounded-full bg-pristine-aqua px-4 py-2"
                  >
                    <Icon className="h-4 w-4 text-pristine-blue" strokeWidth={1.8} />
                    <span className="text-sm font-semibold text-pristine-navy">{f.label}</span>
                  </motion.div>
                  {i < flow.length - 1 && <ChevronRight className="h-4 w-4 mx-1 text-pristine-aquadeep shrink-0" />}
                </Fragment>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.15}>
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_24px_60px_rgba(15,58,112,0.14)] aspect-video bg-pristine-navy">
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/UPYu7gkysfQ"
              title="BioCleanse"
              loading="lazy"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              data-testid="systems-approach-video"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
